import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { apiService, agentApiService, Customer, Stats, WhatsAppStatus, Message } from './services/api';
import socketService from './services/socket';
import Login from './Login';
import { DashboardProvider } from './context/DashboardContext';
import { AppShell } from './components/layout/AppShell';
import { BrowserRouter } from 'react-router-dom';

const defaultWhatsappStatus: WhatsAppStatus = {
  isReady: false,
  isAuthenticated: false,
  sessionId: 'default',
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus>(defaultWhatsappStatus);
  const [stats, setStats] = useState<Stats>({
    totalCustomers: 0,
    customersWithCards: 0,
    totalMessages: 0,
    recentMessages: 0,
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsCheckingAuth(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        // Only remove token if it's actually invalid (401)
        console.log('Token invalid, logging out');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else {
        // For other errors (500, network issues), keep token and try to authenticate
        console.warn('Auth check failed with status:', response.status, '- keeping token');
        setIsAuthenticated(true); // Assume authenticated on network errors
      }
    } catch (error) {
      // Network error - keep the token and assume authenticated
      console.warn('Auth check network error - keeping session:', error);
      setIsAuthenticated(true);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLoginSuccess = (token: string) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    socketService.disconnect();
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusData, statsData, customersData, messagesData] = await Promise.all([
        apiService.getStatus(),
        apiService.getStats(),
        apiService.getCustomers(),
        agentApiService.getAgentConversationThreads(),
      ]);

      setWhatsappStatus(statusData.whatsapp ?? defaultWhatsappStatus);
      setStats(statsData);
      setCustomers(customersData);
      setRecentMessages(
        messagesData.map((thread) => ({
          _id: `${thread.chatId}-${thread.lastUpdated}`,
          customerId: thread.customer?._id || thread.chatId,
          phoneNumber:
            thread.customer?.phoneNumber ||
            thread.customer?.externalContactId ||
            thread.chatId,
          channel: thread.channel || "whatsapp",
          content: thread.lastMessagePreview,
          sender: thread.lastSender === 'agent' ? 'tamiyouz' : 'customer',
          timestamp: thread.lastUpdated,
          whatsappMessageId: undefined,
          isRead: true,
          createdAt: thread.lastUpdated,
        })),
      );
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    const socket = socketService.connect();

    socket.on('whatsapp_status', (status: WhatsAppStatus | undefined) => {
      setWhatsappStatus(status ?? defaultWhatsappStatus);
    });

    socket.on('whatsapp_qr', (qrCode: string) => {
      setWhatsappStatus((prev) => ({ ...prev, qrCode }));
    });

    socket.on('whatsapp_ready', () => {
      loadData();
    });

    socket.on('new_message', () => {
      loadData();
    });

    socket.on('channel_message', () => {
      loadData();
    });

    socket.on('channel_status_update', () => {
      loadData();
    });

    socket.on('customer_update', () => {
      loadData();
    });
  }, [loadData]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      setupWebSocket();
    }

    return () => {
      socketService.disconnect();
    };
  }, [isAuthenticated, loadData, setupWebSocket]);

  if (isCheckingAuth) {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner"></div>
          <span>Checking authentication...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <BrowserRouter>
      <DashboardProvider
        value={{
          stats,
          customers,
          recentMessages,
          whatsappStatus,
          loading,
          refresh: loadData,
        }}
      >
        <AppShell onLogout={handleLogout} />
      </DashboardProvider>
    </BrowserRouter>
  );
}

export default App;
